/**
 * Analytics utility for pushing events to GTM dataLayer
 * These events will be picked up by GTM and forwarded to Meta Pixel
 */

// Ensure dataLayer exists
const getDataLayer = (): Record<string, unknown>[] => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || []
    return window.dataLayer
  }
  return []
}

// Check if user has given consent
const hasConsent = (): boolean => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('cookie_consent') === 'accepted'
  }
  return false
}
interface ProductData {
  id: string
  name: string
  price: number
  currency?: string
  category?: string
}
interface CheckoutData {
  items: ProductData[]
  totalValue: number
  currency?: string
}

/**
 * Track page view event
 */
export const trackPageView = (pagePath: string, pageTitle?: string): void => {
  if (!hasConsent()) return // Don't track if no consent

  const dataLayer = getDataLayer()
  dataLayer.push({
    event: 'page_view',
    page_path: pagePath,
    page_title: pageTitle || document.title,
  })
}

/**
 * Track when user adds item to cart
 */
export const trackAddToCart = (product: ProductData): void => {
  if (!hasConsent()) return // Don't track if no consent

  const dataLayer = getDataLayer()
  dataLayer.push({
    event: 'add_to_cart',
    ecommerce: {
      currency: product.currency || 'CZK',
      value: product.price,
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          price: product.price,
          quantity: 1,
          item_category: product.category || 'training_plan',
        },
      ],
    },
  })
}

/**
 * Track when user initiates checkout (clicks payment button)
 */
export const trackInitiateCheckout = (data: CheckoutData): void => {
  if (!hasConsent()) return // Don't track if no consent

  const dataLayer = getDataLayer()
  dataLayer.push({
    event: 'begin_checkout',
    ecommerce: {
      currency: data.currency || 'CZK',
      value: data.totalValue,
      items: data.items.map((item) => ({
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        quantity: 1,
        item_category: item.category || 'training_plan',
      })),
    },
  })
}

/**
 * Track lead generation (free tips button click)
 */
export const trackLead = (
  contentName: string,
  contentCategory?: string,
): void => {
  if (!hasConsent()) return // Don't track if no consent

  const dataLayer = getDataLayer()
  dataLayer.push({
    event: 'generate_lead',
    content_name: contentName,
    content_category: contentCategory || 'free_tips',
  })
}

/**
 * Track generic custom event
 */
export const trackCustomEvent = (
  eventName: string,
  eventData?: Record<string, unknown>,
): void => {
  const dataLayer = getDataLayer()
  dataLayer.push({
    event: eventName,
    ...eventData,
    consent_granted: hasConsent(),
  })
}
